import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

// const BASE_URL = 'http://localhost:5000';
const BASE_URL = 'http://192.168.0.57:5000';
const EMPTY_FORM = { leasing_carname: '', leasing_cardetails: '' };

export default function ManageLeasingCar() {
  const navigate = useNavigate();

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Modal ──
  const [modalOpen,  setModalOpen]  = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [imageFile,  setImageFile]  = useState(null);
  const [preview,    setPreview]    = useState('');
  const [existingImage, setExistingImage] = useState('');
  const fileRef = useRef();

  // ── Details view modal ──
  const [detailsModal, setDetailsModal] = useState(null); // { name, details }

  // ── Auth ──
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login');
  }, []);

  // ── Fetch ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/leasing-cars', { params: { page: pg, limit: 10, search: q } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load leasing cars'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Open Add ──
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setImageFile(null);
    setPreview('');
    setExistingImage('');
    setModalOpen(true);
  };

  // ── Open Edit ──
  const openEdit = (row) => {
    setForm({
      leasing_carname:    row.leasing_carname    || '',
      leasing_cardetails: row.leasing_cardetails || '',
    });
    setEditId(row.leasing_id);
    setImageFile(null);
    setExistingImage(row.leasing_carimage || '');
    setPreview(row.leasing_carimage ? `${BASE_URL}/images/CarImage/${row.leasing_carimage}` : '');
    setModalOpen(true);
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleImageChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.leasing_carname.trim()) { toast.warning('Car name is required'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('leasing_carname',    form.leasing_carname.trim());
      fd.append('leasing_cardetails', form.leasing_cardetails || '');
      if (imageFile)      fd.append('leasing_carimage', imageFile);
      if (existingImage)  fd.append('existing_carimage', existingImage);

      if (editId) {
        await API.put(`/leasing-cars/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Leasing car updated successfully');
      } else {
        await API.post('/leasing-cars', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Leasing car added successfully');
      }
      setModalOpen(false);
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this leasing car?')) return;
    try {
      await API.delete(`/leasing-cars/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <Layout activePage="Manage Leasing Cars">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Manage Leasing Cars</h2>
          <p style={S.subtitle}>Add and manage leasing vehicle listings</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}>➕ Add Leasing Car</button>
      </div>

      {/* Search */}
      <div style={S.searchWrap}>
        <span style={{ fontSize: 14, color: '#aaa' }}>🔍</span>
        <input type="text" placeholder="Search car name..."
          value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
        {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
      </div>

      {/* Table Card */}
      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Car Image</th>
                <th style={S.th}>Car Name</th>
                <th style={S.th}>Car Details</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={5} style={S.emptyCell}>No leasing cars found</td></tr>
              ) : data.map((row, i) => (
                <tr key={row.leasing_id} style={S.tr}>
                  <td style={S.td}>
                    <span style={S.serial}>{(page - 1) * 10 + i + 1}</span>
                  </td>

                  {/* Image */}
                  <td style={S.td}>
                    {row.leasing_carimage
                      ? <img
                          src={`${BASE_URL}/images/CarImage/${row.leasing_carimage}`}
                          alt={row.leasing_carname}
                          style={{ width: 80, height: 56, objectFit: 'cover', borderRadius: 8,
                            border: '1px solid #eee' }}
                        />
                      : <div style={S.noImg}>🚗</div>}
                  </td>

                  {/* Name */}
                  <td style={S.td}>
                    <span style={S.nameChip}>{row.leasing_carname}</span>
                  </td>

                  {/* Details — truncated with View button */}
                  <td style={{ ...S.td, maxWidth: 260 }}>
                    {row.leasing_cardetails ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: '#888',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', maxWidth: 180,
                          display: 'inline-block' }}
                          dangerouslySetInnerHTML={{ __html: row.leasing_cardetails.replace(/<[^>]+>/g, ' ') }}
                        />
                        <button style={S.viewDetailsBtn}
                          onClick={() => setDetailsModal({ name: row.leasing_carname, details: row.leasing_cardetails })}>
                          View
                        </button>
                      </div>
                    ) : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
                  </td>

                  {/* Actions */}
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={S.editBtn} onClick={() => openEdit(row)}>Edit</button>
                    <button style={S.delBtn}  onClick={() => handleDelete(row.leasing_id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10, total)} of ${total}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <div style={S.overlay} onClick={() => !saving && setModalOpen(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                {editId ? '✏️ Edit Leasing Car' : '➕ Add Leasing Car'}
              </h3>
              <button style={S.closeBtn} onClick={() => !saving && setModalOpen(false)}>✕</button>
            </div>

            <div style={S.modalBody}>

              {/* Car Name */}
              <div style={S.fg}>
                <label style={S.label}>Car Name <span style={S.req}>*</span></label>
                <input name="leasing_carname" value={form.leasing_carname}
                  onChange={handleChange} placeholder="e.g. Toyota Prado"
                  style={S.input} />
              </div>

              {/* Car Details (rich text / HTML textarea) */}
              <div style={{ ...S.fg, marginTop: 14 }}>
                <label style={S.label}>Car Details</label>
                <textarea
                  name="leasing_cardetails"
                  value={form.leasing_cardetails}
                  onChange={handleChange}
                  placeholder="Enter car description, features, specs (HTML supported)..."
                  rows={6}
                  style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                />
                <span style={{ fontSize: 11, color: '#aaa' }}>HTML tags are supported (e.g. &lt;b&gt;, &lt;ul&gt;, &lt;li&gt;)</span>
              </div>

              {/* Image Upload */}
              <div style={{ ...S.fg, marginTop: 14 }}>
                <label style={S.label}>Car Image</label>
                <div style={S.fileRow}>
                  <button type="button" style={S.fileBtn}
                    onClick={() => fileRef.current.click()}>
                    📎 {imageFile ? 'Change Image' : preview ? 'Replace Image' : 'Upload Image'}
                  </button>
                  <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif"
                    style={{ display: 'none' }} onChange={handleImageChange} />
                  {imageFile && (
                    <span style={{ fontSize: 11, color: '#1a9e5c' }}>✓ {imageFile.name}</span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: '#aaa' }}>
                  JPG / PNG / GIF, max 2MB — image will be resized to 600×426px
                </span>

                {/* Preview */}
                {preview && (
                  <div style={{ marginTop: 10 }}>
                    <img src={preview} alt="Preview"
                      style={{ width: 180, height: 127, objectFit: 'cover',
                        borderRadius: 10, border: '1px solid #eee' }} />
                    {!imageFile && editId && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Current image (kept unless replaced)</div>
                    )}
                  </div>
                )}
              </div>

            </div>

            <div style={S.modalFoot}>
              <button style={S.cancelBtn} onClick={() => !saving && setModalOpen(false)}>Cancel</button>
              <button style={{ ...S.submitBtn, opacity: saving ? 0.7 : 1 }}
                onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update' : 'Add Car'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Details View Modal ── */}
      {detailsModal && (
        <div style={S.overlay} onClick={() => setDetailsModal(null)}>
          <div style={{ ...S.modalBox, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                🚗 {detailsModal.name}
              </h3>
              <button style={S.closeBtn} onClick={() => setDetailsModal(null)}>✕</button>
            </div>
            <div style={{ ...S.modalBody, fontSize: 14, lineHeight: 1.8, color: '#444' }}>
              <div dangerouslySetInnerHTML={{ __html: detailsModal.details }} />
            </div>
            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setDetailsModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle:    { margin: '4px 0 0', fontSize: 13, color: '#999' },
  addBtn:      { padding: '10px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
    boxShadow: '0 4px 14px rgba(108,99,255,0.35)' },

  searchWrap:  { display: 'flex', alignItems: 'center', gap: 8, background: '#fff',
    border: '1px solid #e8e8e8', borderRadius: 10, padding: '9px 16px',
    marginBottom: 20, maxWidth: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, fontFamily: 'inherit', color: '#333' },
  clearBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },

  card:        { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  theadRow:    { background: '#fafafa' },
  th:          { padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px' },
  tr:          { transition: 'background 0.12s' },
  td:          { padding: '13px 16px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:   { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:      { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  noImg:       { width: 80, height: 56, background: '#f3f2ff', borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 },
  nameChip:    { background: '#f0fff6', color: '#1a9e5c', borderRadius: 20,
    padding: '4px 14px', fontSize: 13, fontWeight: 600 },

  viewDetailsBtn: {
    background: '#f0f8ff', color: '#1a6eb5', border: '1px solid #b8d8f8',
    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
    fontSize: 11, fontWeight: 700, fontFamily: 'inherit', flexShrink: 0
  },
  editBtn:     { marginRight: 6, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '5px 14px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:      { background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8',
    padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },

  pagination:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:   { fontSize: 12, color: '#999' },
  pgBtn:       { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },

  overlay:     { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.55)', zIndex: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox:    { background: '#fff', borderRadius: 20, width: 560, maxWidth: '96vw', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' },
  modalHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody:   { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot:   { display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:    { background: '#ff6b6b22', border: 'none', color: '#ff6b6b',
    borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },
  cancelBtn:   { padding: '9px 20px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn:   { padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  fg:          { display: 'flex', flexDirection: 'column', gap: 5 },
  label:       { fontSize: 12, fontWeight: 600, color: '#555' },
  req:         { color: '#ff6b6b' },
  input:       { padding: '10px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box' },
  fileRow:     { display: 'flex', alignItems: 'center', gap: 10 },
  fileBtn:     { padding: '8px 16px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
};
